import { toNumber } from '../../../utils/numbers';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import convertUnit from '../../../utils/unit-converter';
import { MeteredMaterialType } from '../../../constants/MaterialModels';
import { calculateIceAdjustment } from './calculate-ice-adjustment';
import { DischargeAmountType } from '../../Discharge/DischargeAmountType';

const FEATURE_AUTOMOISTURE_FOR_METERED = isFeatureEnabled('automoisture_for_metered');
const FEATURE_LIVE_MOISTURE_FOR_WEIGHED = isFeatureEnabled('live_moisture_for_weighed');
const FEATURE_AUTO_WASH_WATER_PROPORTION = isFeatureEnabled('auto_wash_water_proportion');

export const getActualWater = (constituent, values, waterUnitSymbol = 'ga', materialStore) => {
  if (!constituent.moisture) {
    return 0;
  }

  const unitFrom = materialStore.getConstituentUnitSymbol(constituent);
  const m = constituent.batchCode
    ? materialStore.getMaterialByBatchCode(constituent.batchCode)
    : materialStore.getMaterialByItemId(Number(constituent.constituentItemId));
  const md = m ? materialStore.materialDetails[m.materialDetailId] : null;
  let moisture = constituent.moisture;

  if (md?.liveMoisture && (FEATURE_AUTOMOISTURE_FOR_METERED || FEATURE_LIVE_MOISTURE_FOR_WEIGHED)) {
    moisture = (md.autoMoistureAmount + (md.changeLimitForMoisture || 1)) || md.autoMoistureMaxAmount;
  }

  if (md?.absorption) {
    moisture = moisture-md.absorption;
  }

  return toNumber(convertUnit(constituent.quantity * (moisture / 100), unitFrom, waterUnitSymbol), 2);
};

export const getWaterPercentValue = (constituent, waterUnitSymbol, materialStore) => {
  const c = { ...constituent };
  const material = c.batchCode
    ? materialStore.getMaterialByBatchCode(c.batchCode)
    : materialStore.getMaterialByItemId(Number(c.constituentItemId));
  const md = material ? materialStore.materialDetails[material.materialDetailId] || {} : {};
  const waterPercent =  md.waterPercent || 0;
  const q = (c.quantity + (c.trim || 0)) * (waterPercent / 100);
  const unitFrom = materialStore.getConstituentUnitSymbol(constituent);

  return toNumber(convertUnit(q, unitFrom, waterUnitSymbol), 2);
};

export const getWCRatio = (values, ticketStore, materialStore) => {
  const waterUnit = ticketStore.getWaterUnit();
  let waterQty = getTotalWater(values, ticketStore, materialStore);
  let cementUnit, cementQty = 0;

  values.mixDesign.forEach(v => {
    if (materialStore.isCement(v.constituentItemId) || materialStore.isCementitious(v.constituentItemId)) {
      cementQty += v.quantityNet;
      cementUnit = ticketStore.getUnitSymbol(v.unitId);
    }
  });

  if (!cementQty || !cementUnit) {
    return 0;
  }

  waterQty = convertUnit(waterQty, waterUnit, cementUnit);

  return toNumber(waterQty / cementQty, 3);
};

export const getTotalWater = (values, ticketStore, materialStore) => {
  if (!values.mixDesign.length) {
    return 0;
  }

  const waterUnit = ticketStore.getWaterUnit();
  let waterQty = 0;
  let iceAdjustment = calculateIceAdjustment(values.ice, values.iceUnit, values.quantityToBeBatched, waterUnit);
  const quantityToBeBatched = Number(values.quantityToBeBatched);
  const waterInTruck = quantityToBeBatched > 0 ? Number(values.waterInTruck || 0) / quantityToBeBatched : 0;

  values.mixDesign.forEach(v => {
    if (materialStore.isWater(v.constituentItemId) || materialStore.isSecondWater(v.constituentItemId)) {
      waterQty += toNumber(convertUnit(v.quantityNet, ticketStore.getUnitSymbol(v.unitId), waterUnit), 2);
    } else if (materialStore.isAdmixture(v.constituentItemId) || (v.batchCode && v.materialType === MeteredMaterialType.Admixture)) {
      waterQty += getWaterPercentValue(v, waterUnit, materialStore);
    } else {
      waterQty += getActualWater(v, values, waterUnit, materialStore);
    }
  });

  return toNumber(waterQty + iceAdjustment + waterInTruck, 2);
};

export const getAllowedWater = (values, ticketStore, materialStore) => {
  const wcRatio = getWCRatio(values, ticketStore, materialStore);
  const waterQty = getTotalWater(values, ticketStore, materialStore);

  return values.mix.waterCementRatio && wcRatio && waterQty
    ? toNumber(((values.mix.waterCementRatio * 100) / (wcRatio * 100)) * waterQty, 2)
    : 0;
};

export const isAllowedWaterExceeded = (values, ticketStore, materialStore) => {
  const total = getTotalWater(values, ticketStore, materialStore);
  const allowed = getAllowedWater(values, ticketStore, materialStore);
  const percent = allowed * 2 / 100;
  return total > allowed + percent || total < allowed - percent;
};

export const getPrimaryDischarge = (sequence) => {
  return sequence ? sequence.discharges.find(v => v.isPrimary && v.order === 1) : undefined;
};

/**
 * Calculate wash water based on amount type
 */
export const calculateWashWater = (primary, totalWater, watersCount) => {
  if (!primary) {
    return 0;
  }

  if (primary.washAmountType === DischargeAmountType.Unit) {
    // For Unit type: if watersCount provided, divide the fixed amount among all waters
    // Otherwise, use the full amount (for total calculations)
    return watersCount > 0 ? primary.washAmount / watersCount : primary.washAmount;
  }

  // For Percent type: calculate as percentage of total water
  return (primary.washAmount * totalWater) / 100;
};

/**
 * Apply drop-based adjustments for allWaterOnFirstDrop and allWashWaterOnLastDrop
 * Legacy version without overflow handling (used when feature flag is OFF)
 */
const applyDropAdjustmentsLegacy = ({ headWater, washWater, sequence, currentDrop, allDrops }) => {
  let adjusted = {
    headWater: toNumber(headWater, 2),
    washWater: toNumber(washWater, 2),
  };

  if (sequence.allWaterOnFirstDrop) {
    if (currentDrop === 1) {
      adjusted.headWater = toNumber(headWater * allDrops, 2);
    } else {
      adjusted.headWater = 0;
    }
  }

  if (sequence.allWashWaterOnLastDrop) {
    if (currentDrop === allDrops) {
      adjusted.washWater = toNumber(washWater * allDrops, 2);
    } else {
      adjusted.washWater = 0;
    }
  }

  return adjusted;
};

/**
 * Apply drop-based adjustments for allWaterOnFirstDrop and allWashWaterOnLastDrop
 */
export const applyDropAdjustments = ({
  headWater,
  washWater,
  sequence,
  currentDrop,
  allDrops,
  waterScaleCapacity,
  totalHeadWaterForAllWaters,
}) => {
  if (
    !FEATURE_AUTO_WASH_WATER_PROPORTION ||
    /*
    * Auto wash-water proportion doesn't make sense if both
    * "Batch all head water on first drop" and "Batch all wash water on last drop"
    * are turned off.
    */
    !sequence.allWaterOnFirstDrop ||
    !sequence.allWashWaterOnLastDrop
  ) {
    // The legacy version doesn't use auto wash-water proportion.
    return applyDropAdjustmentsLegacy({ headWater, washWater, sequence, currentDrop, allDrops });
  }

  let adjusted = {
    headWater: toNumber(headWater, 2),
    washWater: toNumber(washWater, 2),
  };

  // How much of a true safety margin needs to be reached to consider water overflow
  const WATER_SCALE_OVERFLOW_SAFETY_MARGIN = 0.9;

  const isFirstDrop = currentDrop === 1;
  const isLastDrop = currentDrop === allDrops;
  const totalHeadWaterForThisWater = headWater * allDrops;
  const waterScaleCapacityWithSafetyMargin = waterScaleCapacity * WATER_SCALE_OVERFLOW_SAFETY_MARGIN;

  // Overflow handling: How much (if any) head water exceeded scale capacity – for all water materials
  let totalOverflow = waterScaleCapacity ? (totalHeadWaterForAllWaters - waterScaleCapacityWithSafetyMargin) : 0;
  // Overflow can't be negative - prevent negative values:
  totalOverflow = Math.max(totalOverflow, 0);
  const overflowShare = totalHeadWaterForThisWater / totalHeadWaterForAllWaters;
  const maxHeadWaterDueToOverflow = waterScaleCapacityWithSafetyMargin * overflowShare;
  const overflowForThisWater = totalOverflow * overflowShare;

  if (sequence.allWaterOnFirstDrop) {
    if (isFirstDrop) {
      if (totalOverflow > 0) {
        adjusted.headWater = toNumber(maxHeadWaterDueToOverflow, 2);
      } else {
        adjusted.headWater = toNumber(totalHeadWaterForThisWater, 2);
      }
    } else {
      adjusted.headWater = 0;
    }
  }

  if (sequence.allWashWaterOnLastDrop) {
    if (isLastDrop) {
      adjusted.washWater = toNumber((washWater * allDrops) + overflowForThisWater, 2);
    } else {
      adjusted.washWater = 0;
    }
  }

  return adjusted;
};

/**
 * Calculate water totals for a batch (head/wash split, per-drop values)
 */
export const calculateWaterTotals = ({
  waters,
  dropSize,
  allDrops,
  sequence,
  waterUnit,
  outputUnit,
}) => {
  if (!waters.length) {
    return {
      mftPerDrop: 0,
      totalWater: 0,
      totalWashWater: 0,
      totalHeadWater: 0,
      washWaterPerDrop: 0,
      headWaterPerDrop: 0,
    };
  }

  const getQuantity = (water) => {
    const qty = water.quantityNet || 0;
    if (waterUnit && outputUnit) {
      return convertUnit(qty, waterUnit, outputUnit);
    }
    return qty;
  };

  const totalWaterQuantityDrop = waters.reduce((sum, water) => sum + getQuantity(water), 0);
  const mftPerDrop = totalWaterQuantityDrop * dropSize;
  const primary = getPrimaryDischarge(sequence);
  const totalWater = mftPerDrop * allDrops;
  const totalWashWater = calculateWashWater(primary, totalWater, 0);
  const totalHeadWater = totalWater - totalWashWater;

  const washWaterPerDrop = calculateWashWater(primary, mftPerDrop, waters.length);
  const headWaterPerDrop = mftPerDrop - washWaterPerDrop;

  return {
    mftPerDrop,
    totalWater,
    totalWashWater,
    totalHeadWater,
    washWaterPerDrop,
    headWaterPerDrop,
  };
};
